import React, { useState } from "react";
import { Mail, Loader2, Check } from "lucide-react";
import { apiClient } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

/**
 * Tiny email-capture card shown below the "Talk to an advisor" CTA on each
 * calculator's results panel. Calls POST /api/leads/partial.
 */
export const EmailCapture = ({ source, sourceContext = {} }) => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | sent | error
  const [error, setError] = useState("");

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setError("");
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError("Please enter a valid email.");
      return;
    }
    setState("loading");
    try {
      await apiClient.post("/leads/partial", {
        email: trimmed,
        source: source || "email_capture",
        source_context: sourceContext,
      });
      trackEvent('email_capture_submit', { source: source || 'email_capture' });
      setState("sent");
    } catch (e) {
      setState("error");
      setError("Something went wrong — please try again.");
    }
  };

  if (state === "sent") {
    return (
      <div
        data-testid="email-capture-sent"
        className="mt-4 rounded-xl border border-lw-gold/40 bg-lw-gold/8 p-4 flex items-start gap-3"
        style={{ background: "rgba(212,162,76,0.08)" }}
      >
        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-lw-gold/20 text-lw-navy flex-shrink-0">
          <Check size={16} />
        </span>
        <div>
          <p className="font-sans-ui text-sm font-semibold text-lw-navy">
            Saved — check your inbox.
          </p>
          <p className="text-xs text-lw-muted mt-1">
            We've sent your estimate so you can revisit it any time.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="email-capture-form"
      className="mt-4 rounded-xl border border-lw-navy/10 bg-lw-ivory-warm/60 p-4 sm:p-5"
    >
      <div className="flex items-start gap-2 mb-3">
        <Mail size={16} className="text-lw-gold mt-0.5 flex-shrink-0" />
        <div>
          <span className="block font-sans-ui text-sm font-semibold text-lw-navy">
            Get your personalized plan emailed to you
          </span>
          <span className="block text-xs text-lw-muted mt-0.5">
            We'll send your estimate so you can come back to it later. No
            spam, no sharing.
          </span>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          data-testid="email-capture-input"
          className="flex-1 px-4 py-2.5 rounded-full border border-lw-navy/15 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-lw-gold/50 focus:border-lw-gold"
        />
        <button
          type="submit"
          disabled={state === "loading"}
          data-testid="email-capture-submit"
          className="btn-gold text-sm py-2.5 disabled:opacity-60"
        >
          {state === "loading" ? (
            <>
              <Loader2 className="animate-spin" size={14} /> Sending…
            </>
          ) : (
            <>Send me my plan</>
          )}
        </button>
      </div>
      {error && (
        <span
          data-testid="email-capture-error"
          className="mt-2 block text-xs text-red-600"
        >
          {error}
        </span>
      )}
    </form>
  );
};

export default EmailCapture;
